import config from "appConfig";

const theory = ` <h3 class=sectionHead>What will you learn in this lesson?</h3>
<p>In this lesson, you will first learn some fundamental properties of sound sources, namely, their <strong><span class=cmbx-10>sound power</span></strong>, which indicates how much energy they radiate per second, and their <strong><span class=cmbx-10>directivity</span></strong>, which shows which directions are favoured by the source when it emits sound. You will also learn what <strong><span class=cmbx-10>point sources</span></strong> and <strong><span class=cmbx-10>line </span><span class=cmbx-10>sources</span></strong> are, and how quickly the resulting <strong><span class=cmbx-10>sound pressure</span></strong> drops off with the distance from them. All these properties depend purely on the source, and have nothing to do with the room they may be in. The sound pressure component created purely by the sound source, without the effect of the room, is called the <strong><span class=cmbx-10>direct field</span></strong> or <strong><span class=cmbx-10>free field</span></strong>.<br class=newline>
<p>Next, you will learn that a sound source placed inside a room creates an additional sound pressure component called the <strong><span class=cmbx-10>reverberant field</span></strong> or <strong><span class=cmbx-10>diffuse </span><span class=cmbx-10>field</span></strong>. It is caused by many reflections of sound from the walls, floors, ceiling and objects in the room as the response of the room to sound emitted by the sound source. In the first approximation, the sound pressure level of the reverberant field is constant throughout the room, and it <strong><span class=cmbx-10>does not </span><span class=cmbx-10>depend on the source-to-receiver distance</span></strong>. It <strong><span class=cmbx-10>depends</span></strong> only <strong><span class=cmbx-10>on the </span><span class=cmbx-10>absorption present in the room</span></strong> and <strong><span class=cmbx-10>the sound power of the source</span></strong>.<br class=newline>
<p><strong><span class=cmbx-10>The total sound field in a room is the sum of the direct field and the </span><span class=cmbx-10>reverberant field.</span></strong><br class=newline>
<p>Then you will learn about the <strong><span class=cmbx-10>boundary between the direct and </span><span class=cmbx-10>reverberant sound field</span></strong>, where both components are equal. This boundary is represented with <strong><span class=cmbx-10>hall radius</span></strong> or <strong><span class=cmbx-10>critical distance</span></strong>.<br class=newline>
<p>You will also learn how to calculate direct, reverberant and total sound pressure level in the room, as well as hall radius and critical distance, from known parameters of the source and the room.<br class=newline>
<p>Finally, a refinement of the theory will be given to take into account that the sound pressure level of reverberant sound does slightly depend on the distance from the source.
<h3 class=sectionHead>Introduction</h3>
<p>When a sound source is placed into a closed space, i.e. a room, it will interact with that room in a complex manner. To understand the nature of that interaction, one has to account for the properties of the sound source, as well as the properties of the room itself.<br class=newline>
<p>The following considerations are valid for the so-called Sabine rooms, i.e. rooms with all three dimensions similar to each other, with the ratio of the largest to the smallest dimension not greater than 3. Spaces that do not meet this criteria, e.g. industrial halls or long hallways, may behave a bit differently. Additionally, these considerations do not take into account the phenomena that disrupt the formation of the sound field in a room in the manner explained here, the most prominent ones being room resonance with resulting room modes, or local defects such as sound focusing.
<h3 class=sectionHead>Point and line sources</h3>
<p>The sources of interest in room acoustics can be divided into two main groups.<br class=newline>
<p><strong><span class=cmbx-10>Point sources</span></strong> essentially <strong><span class=cmbx-10>radiate from a single point in space</span></strong>, and the resulting wavefront is spherical. As the surface area of a sphere increases with the square of its radius, the sound intensity decreases with the square of the distance from the source. The resulting <strong><span class=cmbx-10>sound pressure level </span><span class=cmbx-10>drops 6 dB for every doubling of</span></strong> that <strong><span class=cmbx-10>distance</span></strong>. This is called <strong><span class=cmbx-10>the </span><span class=cmbx-10>inverse-square law</span></strong>. The sources that belong to this group are virtually all musical instruments, vocalists, speakers, as well as loudspeaker enclosures, HVAC units, etc.<br class=newline>
<p><strong><span class=cmbx-10>Line sources</span></strong> are represented with <strong><span class=cmbx-10>a line in space</span></strong>, rather than a point. Therefore, the sound waves propagating from such a source will form a cylindrical wave front that represents the lateral surface of a cylinder. As the corresponding surface area increases with the radius of that cylinder, the sound intensity also decreases with the distance from the source, but slower than it does for point sources. The resulting <strong><span class=cmbx-10>sound pressure level drops 3 dB for every </span><span class=cmbx-10>doubling of distance</span></strong>. The sources of interest that belong to this group are line arrays of loudspeakers used in sound reinforcement (to some extent, as their dimensions are finite). Another example of a line source (not so relevant for room acoustics) are roads with sufficiently high traffic flow.<br class=newline>
<p>Both types of sources are illustrated in <a href=# data-img-alt="Figure 1. Point source (left) and line source (right)." data-img-src=${config.ASSETS_URL}/05/06/theory/figure-1.svg>Figure 1</a>.<br class=newline>
<p>
   In further considerations, point sources will be used as the reference.
   <figure class=figure>
<p><img alt="Figure 1. Point source (left) and line source (right)." src=${config.ASSETS_URL}/05/06/theory/figure-1.svg><figcaption class=caption>Figure 1. Point source (left) and line source (right).</figcaption></figure>
<h3 class=sectionHead>Near field and far field of a source</h3>
<p>The space around any source, be it a sound source or a source of some other type of waves, can be divided into two regions: the so-called near-field region and the far-field region.<br class=newline>
<p><strong><span class=cmbx-10>All phenomena described in this lesson are observed in the far field of </span><span class=cmbx-10>a source.</span></strong><br class=newline>
<p>The <strong><span class=cmbx-10>near field</span></strong> is formed <strong><span class=cmbx-10>very close to the sound source itself</span></strong>, but also in the close proximity of large, rigid surfaces such as walls, ceilings and floors in rooms as secondary sources. Its main characteristics is that the sound pressure level of sound emitted by the source does not follow any rules of propagation, and it is very difficult, if not impossible to predict its behaviour. For this reason, no measurements based on sound pressure are made in the near-field region of the source. The extent of this region depends primarily on the physical size of the source and the wavelength of the lowest frequency the source emits.<br class=newline>
<p><strong><span class=cmbx-10>Beyond the near-field region</span></strong> extends the <strong><span class=cmbx-10>far-field</span></strong> region. In this region, the sound follows the usual rules of propagation.
<h3 class=sectionHead>Free (direct) field and diffuse (reverberant) field</h3>
<p>When a sound source is put in a space that is free from obstacles the sound would reflect or diffract from, the sound emitted by that source is allowed to propagate freely, and the resulting sound field is called the <strong><span class=cmbx-10>free field</span></strong> or the <strong><span class=cmbx-10>direct field</span></strong> of that source. The sound propagates away from the source and its intensity and the resulting sound pressure level decrease with distance, as explained above.<br class=newline>An example of a sound source creating a free field is an airliner flying at cruise altitude. However, to truly experience that free field, the listener would also have to be far enough from all potentially reflecting surfaces, i.e. high above ground.<br class=newline>An example of a controlled space capable of supporting practically ideal free-field conditions is the anechoic room. All the surfaces of such a room are heavily lined with absorbing material that absorbs virtually all incoming sound, thus preventing it from being reflected back into the room.<br class=newline>
<p>The <strong><span class=cmbx-10>diffuse field</span></strong> or <strong><span class=cmbx-10>reverberant field</span></strong> is formed in quite the opposite manner. For it to form, the sound source has to be placed in a room with reflective surfaces. With the source in operation, the sound emitted by it will be reflected many times from the surfaces of the room and from the objects inside it. All these reflections put together will form the diffuse (reverberant) field in the room. A perfectly diffuse field would have constant sound pressure level of reverberant sound throughout the room. This is an ideal that cannot be achieved in practice, but it will be used in further considerations nonetheless. The diffuse field will form in any room (other than the anechoic room), to the extent that approaches the ideal to some degree. In highly reverberant rooms with hard, reflective surfaces, the reverberant sound will be distributed/diffused more evenly over the whole room due to a very large number of reflections. With more absorption introduced into the room, reverberant sound will be spread unevenly in the room.<br class=newline>
<p>
   Different types of sound fields that are formed inside a room with a sound source are shown in <a href=# data-img-alt="Figure 2. Different types of sound fields inside a room." data-img-src=${config.ASSETS_URL}/05/06/theory/figure-2.svg>Figure 2</a>. As stated above, all considerations in this lesson are valid for the far field region of both the source and the reflecting surfaces, i.e. far enough from the source and the surfaces of the room.
   <figure class=figure>
<p><img alt="Figure 2. Different types of sound fields inside a room." src=${config.ASSETS_URL}/05/06/theory/figure-2.svg><figcaption class=caption>Figure 2. Different types of sound fields inside a room.</figcaption></figure>
<h3 class=sectionHead>Comparison of the behaviour of sound and heat</h3>
<p>Although the underlying mechanisms are not the same, the behaviour of sound in a room has some common traits with the behaviour of heat, and can be illustrated that way.<br class=newline>
<p>A <strong><span class=cmbx-10>heat source</span></strong> put into a room has a certain <strong><span class=cmbx-10>thermal power</span></strong>. For example, it can be an electric heater with the nominal power of 2 kilowatts, or a gas heater with the power of 5 kilowatts, or an open fire (not very likely, but possible) of unknown power.<br class=newline>
<p><strong><span class=cmbx-10>Whatever the power of a heat source is, it is solely the property of the </span><span class=cmbx-10>source</span></strong>, in the sense that the source will always generate the same power, regardless of the environment it is put into. <strong><span class=cmbx-10>The same goes for a sound source and its </span><span class=cmbx-10>sound power</span></strong>.<br class=newline>
<p>For example, a 2 kW electric heater will produce those 2 kilowatts anywhere, i.e. in a room, outside, on the North Pole,…<br class=newline>
<p>A <strong><span class=cmbx-10>heat source</span></strong> can also have a certain <strong><span class=cmbx-10>directivity</span></strong>, meaning that it is designed to radiate most of the heat in a desired direction.<br class=newline>
<p>For example, infrared electric heaters are designed to radiate heat into space directly in front of them, and very little of it is “spilled” in other directions. That way, they can be placed or mounted close to walls and other surfaces or objects that would otherwise suffer from exposure to excessive heat.<br class=newline>
<p>An open fire, on the other hand, radiates heat evenly in all directions (at least in the horizontal plane, around the fire).<br class=newline>
<p><strong><span class=cmbx-10>Whatever the directivity of a heat source is, it is solely the property of </span><span class=cmbx-10>the source</span></strong>, in the sense that the source will maintain its directional properties regardless of the environment. <strong><span class=cmbx-10>The same can be said for sound sources and </span><span class=cmbx-10>their directivity</span></strong>.<br class=newline>
<p>For example, the said infrared heater will direct its radiation in the forward direction anywhere, i.e. in a room, in the yard outside, on the North Pole,…<br class=newline>
<p><strong><span class=cmbx-10>The effect</span></strong> a <strong><span class=cmbx-10>heat source</span></strong> produces in the environment is measurable; all that needs to be done is to take a thermometer and measure <strong><span class=cmbx-10>the temperature</span></strong> as the parameter that shows this effect. The same can be said for sound sources, but the parameter that describes <strong><span class=cmbx-10>the effect</span></strong> of a <strong><span class=cmbx-10>sound source</span></strong> is <strong><span class=cmbx-10>sound pressure (level)</span></strong>, which can also be measured quite simply and effectively, using a sound level meter.<br class=newline>
<p><strong><span class=cmbx-10>The effect produced by a heat source or a sound source will </span><span class=cmbx-10>depend both on the properties of a source and on the environment.</span></strong><br class=newline>
<p>A source has to have <strong><span class=cmbx-10>sufficient power</span></strong> to produce the desired effect. For example, a room cannot be warmed up by lighting one candle, and one cell phone loudspeaker will not be enough to build a sound reinforcement system on a stadium.<br class=newline>
<p><strong><span class=cmbx-10>The distance from a heat source or a sound source</span></strong> will also play a role, especially outdoors. For example, if one builds a bonfire, those sitting too close to it might get burnt, those sitting at the right distance will feel a pleasant warmth, and those sitting too far away from it will be cold. For a sound source such as a public address (PA) loudspeaker, people very close to it will be overwhelmed by loud sound, people standing further away will listen to music at a pleasant level, and people being very far from the loudspeaker will struggle to hear what is going on.<br class=newline>
<p><strong><span class=cmbx-10>The directivity of a heat source or a sound source</span></strong> is also a factor. For example, if a person is standing right in front of an infrared electric heater at 1 m distance, they might eventually get burnt, whereas those standing to the side or behind the heater at the same distance will feel very little heat coming from the heater. If a person is standing at some distance in front of a directional sound source such as a trumpet or a horn loudspeaker, the sound coming from the source will be much louder than it would be if the person was standing at the same distance, but to the side or behind the trumpet player (or the loudspeaker).<br class=newline>
<p>For a <strong><span class=cmbx-10>heat source</span></strong> placed in a room, <strong><span class=cmbx-10>the size and thermal properties of the </span><span class=cmbx-10>room</span></strong> play a crucial role.<br class=newline>
<p>For a <strong><span class=cmbx-10>sound source</span></strong> placed in a room, <strong><span class=cmbx-10>the size and acoustical properties of </span><span class=cmbx-10>the room</span></strong> play a crucial role.<br class=newline>
<p>For example, a bathroom heater will be more than enough to heat up a bathroom, but that same heater could never heat up a concert hall or a church. In the same manner, a cell phone loudspeaker will be loud enough to be heard well in a living room, but it will not be loud enough to be heard by everyone in a concert hall or a large warehouse.<br class=newline>
<p>The <strong><span class=cmbx-10>thermal properties of a room</span></strong> are described with <strong><span class=cmbx-10>heat loss through its </span><span class=cmbx-10>surfaces</span></strong>.<br class=newline>
<p>The final ambient temperature reached in the room (far enough from the source) will depend on the power of the heat source, and on the amount of heat loss.<br class=newline>
<p><strong><span class=cmbx-10>Well-insulated rooms will have very little heat loss</span></strong>, and a source of appropriate power will be more than enough to heat up the entire room to a pleasant temperature. Moreover, the temperature will most likely be the same in the whole room (not taking into account the change with height), except close to the source, where the heat coming from the source will be felt directly.<br class=newline>
<p><strong><span class=cmbx-10>Poorly insulated rooms will have significant heat loss</span></strong>, e.g. in houses with no façade of any kind, with thin single-pane windows that are not sealed properly, with holes in the walls, etc. If the same heat source as above is put into a room of the same size as above, but poorly insulated, it will spend much more of its energy to cover the loss, and will not be able to heat up the room properly. The temperature in the room will not be high enough to make people feel pleasantness. One might find that the ambient temperature drops a bit as they move away from the source, even inside a room, for example, as they approach a leaky window.<br class=newline>
<p>If an open fire is built in the middle of the room (not likely, but just as an example), people sitting in a circle close to it will feel hot, the ones sitting in a circle behind them will still feel warm, and the ones sitting in another circle further away will feel only the ambient heat in the room.<br class=newline>
<p>If a directional heat source is put into a room, such as an infrared heater, the heat coming from it in the frontal direction will surely be felt even relatively far away from it, and the user will have to move quite far away to feel only the ambient heat in the room. If the user is standing to the side of the heater or behind it, very little or no heat will come directly from the heater, and the user will feel mostly (or only) ambient heat, even when standing close to the heater.<br class=newline>
<p>The <strong><span class=cmbx-10>acoustical properties of a room</span></strong> are described with <strong><span class=cmbx-10>the loss of sound </span><span class=cmbx-10>energy through absorption in the finishing materials that cover its </span><span class=cmbx-10>surfaces</span></strong>.<br class=newline>
<p><strong><span class=cmbx-10>The final sound pressure level of reverberant sound reached in the </span><span class=cmbx-10>room (far enough from the source) will depend on the power of the </span><span class=cmbx-10>sound source, and on the amount of sound energy loss (absorption).</span></strong><br class=newline>
<p><strong><span class=cmbx-10>Rooms with hard, reflective surfaces (concrete, marble, ceramic tiles, </span><span class=cmbx-10>etc.) that absorb very little sound will have very low sound energy </span><span class=cmbx-10>loss</span></strong>, and a sound source of appropriate power will be more than enough to produce loud reverberant sound in the entire room. The sound pressure level of reverberant sound will most likely be the same in the whole room, except close to the source, where the direct sound coming from the source will be dominant.<br class=newline>
<p><strong><span class=cmbx-10>Rooms that have been acoustically treated (dampened) will have </span><span class=cmbx-10>significant sound energy loss</span></strong>, e.g. if <strong><span class=cmbx-10>thick curtains</span></strong> are placed on the walls, or a <strong><span class=cmbx-10>heavy carpet</span></strong> on the floor, or a <strong><span class=cmbx-10>suspended ceiling with perforated gypsum </span><span class=cmbx-10>plates</span></strong>, etc. If the same sound source as above is put into a room of the same size as above, but acoustically treated, it will spend much more of its energy just to cover the loss caused by sound absorption, and the resulting sound pressure level of reverberant sound will be much lower, i.e. the reverberant sound will be softer (and shorter).<br class=newline>
<p>Although the theory suggests that the reverberant sound is equally loud in the entire room, in acoustically treated rooms it actually drops a bit as the listener moves away from the source.<br class=newline>
<p>If an omnidirectional source, e.g. a standard source for acoustic measurements, is put in the middle of the room, people sitting in a circle close to it will hear quite loud direct sound, the ones sitting in a circle behind them will hear softer direct sound of the source, and the ones sitting in another circle far enough will hear the prevailing reverberant sound.<br class=newline>
<p>If a directional sound source, such as a PA loudspeaker is put into a room, the direct sound coming from it in the forward direction will surely be dominant over the reverberant sound even relatively far away from it, and the listener will have to move quite far away to hear dominantly reverberant sound. If the listener is standing to the side or behind such a loudspeaker, very little direct sound will come directly from the loudspeaker, and the listener will hear mostly reverberant sound, even close to the loudspeaker.<br class=newline>
<h3 class=sectionHead>Sound source in a room</h3>
<p>In previous lessons, it has been established that the sound emitted by a source inside an ordinary room consists of three components: <strong><span class=cmbx-10>direct sound</span></strong>, <strong><span class=cmbx-10>early reflections</span></strong> and <strong><span class=cmbx-10>reverberant tail</span></strong>.
<h4 class=subsectionHead>Direct sound field</h4>
<p>The direct sound is the component that travels directly from the source to the listener/receiver. The distance it has to travel is the shortest possible one, so this component arrives first to the listening position. If there is a line of sight between the source and the listener, direct sound will exist. In oddly shaped rooms, e.g. in an L-shaped room, there will be no direct sound if the source and the listener are around the corner from each other. The direct sound is not necessarily the strongest individual component to arrive at the listening position. It may be topped by an early reflection if, e.g. the source is highly directional and is pointed towards a reflective surface instead of being oriented towards the listener.<br class=newline>
<p>The important fact to remember is that <strong><span class=cmbx-10>the direct sound propagates</span></strong> quite <strong><span class=cmbx-10>freely from the source to the listener</span></strong>, and <strong><span class=cmbx-10>there is no influence of the </span><span class=cmbx-10>room</span></strong>. Therefore, the <strong><span class=cmbx-10>direct sound depends only on the properties of the </span><span class=cmbx-10>source</span></strong>.<br class=newline>
<p><strong><span class=cmbx-10>Direct sound also depends on the distance from the source</span></strong>. As explained above, the <strong><span class=cmbx-10>direct sound of a point source drops by 6 dB for every doubling </span><span class=cmbx-10>of the distance</span></strong> from the source.
<h5 class=subsubsectionHead>Omnidirectional source</h5>
<p>
   If an ideal, <strong><span class=cmbx-10>omnidirectional point source</span></strong> is put <strong><span class=cmbx-10>in a room</span></strong>, it will create the free-field component of the sound field represented by <strong><span class=cmbx-10>direct sound</span></strong> that <strong><span class=cmbx-10>will </span><span class=cmbx-10>be equal in all directions</span></strong>. An example of an omnidirectional source is, in fact, any source that is small compared to the wavelength of sound it emits, e.g. a reasonably small low-frequency loudspeaker. Specially designed (nearly)-omnidirectional sources are used in room acoustical measurements. An example of such a source is shown in <a href=# data-img-alt="Figure 3. An example of (nearly) omnidirectional sources: a subwoofer (left), and a source used in acoustic measurements (right)." data-img-src=${config.ASSETS_URL}/05/06/theory/figure-3.svg>Figure 3</a>. Its directivity is represented as a two-dimensional (2D) and/or a three-dimensional (3D) polar response, as shown in <a href=# data-img-alt="Figure 4. Polar response of a (nearly) omnidirectional source: a 2D polar pattern (left), and a 3D balloon (right)." data-img-src=${config.ASSETS_URL}/05/06/theory/figure-4.svg>Figure 4</a>.
   <figure class=figure>
<p><img alt="Figure 3. An example of (nearly) omnidirectional sources: a subwoofer (left), and a source used in acoustic measurements (right)." src=${config.ASSETS_URL}/05/06/theory/figure-3.svg><figcaption class=caption>Figure 3. An example of a (nearly) omnidirectional source used in acoustic measurements.</figcaption></figure><figure class=figure><p><img alt="Figure 4. Polar response of a (nearly) omnidirectional source: a 2D polar pattern (left), and a 3D balloon (right)." src=${config.ASSETS_URL}/05/06/theory/figure-4.svg> <figcaption class=caption>Figure 4. Polar response of a (nearly) omnidirectional source: a 2D polar pattern (left), and a 3D balloon (right).</figcaption></figure>
<p>
   No matter where the listener is, at the distance
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>r</mi>
   </math>
   from the source, the intensity of direct sound will be
<table class=equation>
   <tr>
      <td>
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <mi>I</mi>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mi>P</mi>
               </mrow>
               <mrow>
                  <mn>4</mn>
                  <mi>π</mi>
                  <msup>
                     <mrow>
                        <mi>r</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
               </mrow>
            </mfrac>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(1)
</table>
<p>
   where
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>P</mi>
   </math>
   is the sound power of that source.<br class=newline>
<p>
   To facilitate the direct addition of the contributions of the direct and the reverberant component to the overall sound field, both of them are expressed in terms of energy density
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>ω</mi>
   </math>
   as
<table class=equation>
   <tr>
      <td>
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <mi>ω</mi>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mi>I</mi>
               </mrow>
               <mrow>
                  <mi>c</mi>
               </mrow>
            </mfrac>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(2)
</table>
<p>
   where
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>c</mi>
   </math>
   is the speed of sound.<br class=newline>
<p>
   This yields the energy density of direct sound at a distance
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>r</mi>
   </math>
   from an omnidirectional source as
<table class=equation>
   <tr>
      <td>
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <msub>
               <mrow>
                  <mi>ω</mi>
               </mrow>
               <mrow>
                  <mi>d</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mi>P</mi>
               </mrow>
               <mrow>
                  <mn>4</mn>
                  <mi>π</mi>
                  <msup>
                     <mrow>
                        <mi>r</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
                  <mi>c</mi>
               </mrow>
            </mfrac>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(3)
</table>
<h5 class=subsubsectionHead>Directional source</h5>
<p>A perfectly omnidirectional sound source is an ideal, i.e. a special case that can sometimes be achieved. However, a great majority of sound sources will tend to radiate most of the sound energy in specific directions at the expense of all other directions. This means that the direct sound of such a source will be the loudest in the favoured directions, and softer in the other ones. Whether a directional source is desired or not depends on the source itself. For example, we might want a musical instrument to radiate sound rather evenly in all directions, because that way the room is strongly excited and provides a strong response that enhances the sound of that instrument. On the other hand, if we are designing a sound reinforcement system using loudspeakers, it is highly desirable to radiate as much sound as possible into the audience area, and waste as little of it as possible into other directions. In any case, realistic sources will have directional properties that need to be described and taken into account.<br class=newline>
<p>Examples of directional properties of a sound source are shown in <a href=# data-img-alt="Figure 5. Human talker and the corresponding directivity pattern (broadband = taken over all frequencies of interest)." data-img-src=${config.ASSETS_URL}/05/06/theory/figure-5.svg>Figure 5</a> for a human talker, and in <a href=# data-img-alt="Figure 6. A loudspeaker with the diameter of 20 cm, and a typical directivity pattern (shown for different frequencies)." data-img-src=${config.ASSETS_URL}/05/06/theory/figure-6.svg>Figure 6</a> for a standardly made loudspeaker.<br class=newline>
<p>
   The overall directivity pattern for the talker was displayed for all frequencies of interest. Such a chart does not show how the directivity changes with frequency. Overall, the talker will produce the loudest sound right in front, whereas the sound pressure level will drop by about 3-4 dB to the side, and 6-7 dB behind the speaker.
   <figure class=figure>
<p><img alt="Figure 5. Human talker and the corresponding directivity pattern (broadband = taken over all frequencies of interest)." src=${config.ASSETS_URL}/05/06/theory/figure-5.svg><figcaption class=caption>Figure 5. Human talker and the corresponding directivity pattern (broadband = taken over all frequencies of interest).</figcaption></figure>
<p>
   To show how the directional properties of a sound source change with frequency, several polar patterns are displayed on the same chart, usually for standard octave frequencies (125, 250, 500, 1000, 2000, 4000 and 8000 Hz), as shown in <a href=# data-img-alt="Figure 6. A loudspeaker with the diameter of 20 cm, and a typical directivity pattern (shown for different frequencies)." data-img-src=${config.ASSETS_URL}/05/06/theory/figure-6.svg>Figure 6</a> for a loudspeaker. It can be seen that the directivity of a sound source changes with frequency, in the sense that the source becomes more and more directional as the frequency increases, which is the inherent property of virtually all sound sources. The example in <a href=# data-img-alt="Figure 6. A loudspeaker with the diameter of 20 cm, and a typical directivity pattern (shown for different frequencies)." data-img-src=${config.ASSETS_URL}/05/06/theory/figure-6.svg>Figure 6</a> shows that the source radiates virtually omnidirectionally at 500 Hz. At higher frequencies, its emission becomes more and more directed to the front at the expense of the side direction, where up to 20 dB is lost compared to the frontal direction.<br class=newline>
   <figure class=figure>
<p><img alt="Figure 6. A loudspeaker with the diameter of 20 cm, and a typical directivity pattern (shown for different frequencies)." src=${config.ASSETS_URL}/05/06/theory/figure-6.svg><figcaption class=caption>Figure 6. A loudspeaker with the diameter of 20 cm, and a typical directivity pattern (shown for different frequencies).</figcaption></figure>
<p>
   <strong><span class=cmbx-10>The directional properties of sound sources</span></strong> in general <strong><span class=cmbx-10>are </span><span class=cmbx-10>represented with</span></strong> a quantity called <strong><span class=cmbx-10>gain</span></strong> or
   <strong>
      <span class=cmbx-10>directivity factor</span>
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <mi>Q</mi>
      </math>
   </strong>
   , and the corresponding directivity index
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>D</mi>
         </mrow>
         <mrow>
            <mi>i</mi>
         </mrow>
      </msub>
   </math>
   equal to
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mn>10</mn>
      <mo class=MathClass-bin>∗</mo>
      <mi mathvariant=italic>log</mi>
      <mo class=MathClass-open>(</mo>
      <mi>Q</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   .<br class=newline>
<p>
   The directivity factor (index) for a sound source in general is defined as a function of frequency and of direction represented with azimuth
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>𝜃</mi>
   </math>
   and elevation
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>ϕ</mi>
   </math>
   , i.e.
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>Q</mi>
      <mo class=MathClass-rel>=</mo>
      <mi>Q</mi>
      <mo class=MathClass-open>(</mo>
      <mi>𝜃</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>ϕ</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>f</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   .<br class=newline>
<p>The definition of the directivity factor can be seen in <a href=# data-img-alt="Figure 7. The directivity factor (index) of a directional source." data-img-src=${config.ASSETS_URL}/05/06/theory/figure-7.svg>Figure 7</a>.
<p>
   Basically, if there are two sources of equal sound power
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>P</mi>
   </math>
   , out of which one is directional and the other is omnidirectional, then the directional one will tend to radiate the majority of sound energy in a particular direction (or within a given solid angle) at the expense of all other directions, whereas the omnidirectional source will radiate evenly in all directions.<br class=newline>
<p>
   The directivity factor is defined as the ratio of the sound intensity
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>I</mi>
         </mrow>
         <mrow>
            <mi>d</mi>
         </mrow>
      </msub>
   </math>
   produced by the directional source in a given direction, and the sound intensity
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>I</mi>
         </mrow>
         <mrow>
            <mi>o</mi>
         </mrow>
      </msub>
   </math>
   produced by the omnidirectional source, observed at a fixed distance from the source. Intensity
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>I</mi>
         </mrow>
         <mrow>
            <mi>o</mi>
         </mrow>
      </msub>
   </math>
   can also be understood as the average intensity produced by the directional source, averaged over the entire solid angle of
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mn>4</mn>
      <mi>π</mi>
   </math>
   .
<table class=equation>
   <tr>
      <td>
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <mi>Q</mi>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>I</mi>
                     </mrow>
                     <mrow>
                        <mi>d</mi>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>I</mi>
                     </mrow>
                     <mrow>
                        <mi>o</mi>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(4)
</table>
<figure class=figure>
   <p>
      <img alt="Figure 7. The directivity factor (index) of a directional source." src=${config.ASSETS_URL}/05/06/theory/figure-7.svg>
      <figcaption class=caption>Figure 7. The directivity factor (index) of a directional source.</figcaption>
</figure>
<p>
   The directivity index
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>D</mi>
         </mrow>
         <mrow>
            <mi>i</mi>
         </mrow>
      </msub>
   </math>
   is obtained simply by taking the logarithm of the previous equation, as
<table class=equation>
   <tr>
      <td>
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <msub>
               <mrow>
                  <mi>D</mi>
               </mrow>
               <mrow>
                  <mi>i</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mn>10</mn>
            <mi class=qopname>log</mi>
            <mo>⁡</mo>
            <mo class=MathClass-open>(</mo>
            <mi>Q</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>L</mi>
               </mrow>
               <mrow>
                  <mi>d</mi>
               </mrow>
            </msub>
            <mo class=MathClass-bin>−</mo>
            <msub>
               <mrow>
                  <mi>L</mi>
               </mrow>
               <mrow>
                  <mi>o</mi>
               </mrow>
            </msub>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(5)
</table>
<p>thus turning the ratio of the two intensities into the difference of their intensity levels, and, consequently, the difference between the sound pressure levels each source would give at the given listener position.<br class=newline>
<p>It can be seen that the directivity factor or gain will be greater than 1 in the directions favoured by the directional source, but also smaller than 1 in all other directions. The directivity index will assume positive values in the first case, and negative in the second.<br class=newline>
<p>The influence of source directivity can be added into the original equation for energy density of direct sound as
<table class=equation>
   <tr>
      <td>
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <msub>
               <mrow>
                  <mi>ω</mi>
               </mrow>
               <mrow>
                  <mi>d</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mi mathvariant=italic>PQ</mi>
               </mrow>
               <mrow>
                  <mn>4</mn>
                  <mi>π</mi>
                  <msup>
                     <mrow>
                        <mi>r</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
                  <mi>c</mi>
               </mrow>
            </mfrac>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(6)
</table>
<p>
   having in mind that the directivity factor
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>Q</mi>
   </math>
   is dependent on direction, but also on frequency, as directional properties of sound sources change with frequency.
<h5 class=subsubsectionHead>What does direct sound depend on?</h5>
<p>The governing equations (3) and (6) suggest that <strong><span class=cmbx-10>the “loudness” of direct </span><span class=cmbx-10>sound</span></strong>:
<ul class=itemize1>
   <li class=itemize><strong><span class=cmbx-10>decreases with the distance from the source</span></strong>; the farther you are, the softer the sound
   <li class=itemize><strong><span class=cmbx-10>is proportional to the sound power of the source</span></strong>; the stronger the source, the louder the sound at a given listening position
   <li class=itemize><strong><span class=cmbx-10>greatly depends on the directional properties of the source</span></strong>; at a fixed distance, the sound will be very loud in front of a directional source, less loud to the sides, and even softer behind it
</ul>
<p><strong><span class=cmbx-10>The overall conclusion is that the direct sound field depends only </span><span class=cmbx-10>on the properties of the source, with no influence of the room </span><span class=cmbx-10>whatsoever.</span></strong>
<h4 class=subsectionHead>Reverberant sound field</h4>
<p>The same source that was responsible for generating direct sound will also generate the reverberant sound in the room. It consists of all the components of sound that have been reflected from surfaces in the room at least once. This means that both the early reflections and the reverberant tail form the reverberant sound field, although the early reflections will increase the loudness and improve the clarity of direct sound, from the perceptual point of view.<br class=newline>
<p>The energy equilibrium will be established once the energy introduced into the room by the source becomes equal to the energy lost due to absorption in the surfaces of the room (and, in very small part, to transmission through them), which ultimately converts acoustic energy into heat. This suggests that the rooms with hard, reflective surfaces will tend to have a strong diffuse field and a “loud” reverberant sound. This is due to very little absorption these reflective surfaces provide, and, consequently, very little losses. On the other hand, damped rooms with surfaces made of highly absorptive materials will tend to have weak diffuse field and “soft” reverberant sound due to high losses the source is forced to compensate for.<br class=newline>
<p>The traditional representation of the diffuse sound field [3] states that the energy density of the diffuse field can be expressed as
<table class=equation>
   <tr>
      <td>
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <msub>
               <mrow>
                  <mi>ω</mi>
               </mrow>
               <mrow>
                  <mi>r</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mn>4</mn>
                  <mi>P</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>cA</mi>
               </mrow>
            </mfrac>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mn>4</mn>
                  <mi>P</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>cS</mi>
                  <mover class=mml-overline accent=false>
                     <mrow>
                        <mi>α</mi>
                     </mrow>
                     <mo accent=true>¯</mo>
                  </mover>
               </mrow>
            </mfrac>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(7)
</table>
<p>
   In the equation above,
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>P</mi>
   </math>
   is the sound power of the source,
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>c</mi>
   </math>
   is the speed of sound,
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>A</mi>
   </math>
   is the total absorption in the room expressed in Sabines,
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>S</mi>
   </math>
   is the total surface area of all the surfaces in the room, and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mover class=mml-overline accent=false>
         <mrow>
            <mi>α</mi>
         </mrow>
         <mo accent=true>¯</mo>
      </mover>
   </math>
   is the average absorption coefficient for the entire room.<br class=newline>
<p>
   It has to be taken into account that not all energy introduced into the room will be used for forming the reverberant sound field, but only the energy that has already been reflected once from the surfaces of the room. If the power introduced by the source is
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>P</mi>
   </math>
   , the sound produced by the source will arrive on all the surfaces of the room with total surface area
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>S</mi>
   </math>
   and average absorption coefficient
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mover class=mml-overline accent=false>
         <mrow>
            <mi>α</mi>
         </mrow>
         <mo accent=true>¯</mo>
      </mover>
   </math>
   . After the first reflection, the power
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mover class=mml-overline accent=false>
         <mrow>
            <mi>α</mi>
         </mrow>
         <mo accent=true>¯</mo>
      </mover>
      <mi>P</mi>
   </math>
   will be absorbed, and the remaining power
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mo class=MathClass-open>(</mo>
      <mn>1</mn>
      <mo class=MathClass-bin>−</mo>
      <mover class=mml-overline accent=false>
         <mrow>
            <mi>α</mi>
         </mrow>
         <mo accent=true>¯</mo>
      </mover>
      <mo class=MathClass-close>)</mo>
      <mi>P</mi>
   </math>
   will be reflected back to the room to form the reverberant field.<br class=newline>
<p>The energy density in the reverberant field can now be expressed as
<table class=equation>
   <tr>
      <td>
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <msub>
               <mrow>
                  <mi>ω</mi>
               </mrow>
               <mrow>
                  <mi>r</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mn>4</mn>
                  <mi>P</mi>
                  <mo class=MathClass-open>(</mo>
                  <mn>1</mn>
                  <mo class=MathClass-bin>−</mo>
                  <mover class=mml-overline accent=false>
                     <mrow>
                        <mi>α</mi>
                     </mrow>
                     <mo accent=true>¯</mo>
                  </mover>
                  <mo class=MathClass-close>)</mo>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>cS</mi>
                  <mover class=mml-overline accent=false>
                     <mrow>
                        <mi>α</mi>
                     </mrow>
                     <mo accent=true>¯</mo>
                  </mover>
               </mrow>
            </mfrac>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(8)
</table>
<p>
   The term
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>R</mi>
   </math>
   equal to
<table class=equation>
   <tr>
      <td>
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <mi>R</mi>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mi>S</mi>
                  <mover class=mml-overline accent=false>
                     <mrow>
                        <mi>α</mi>
                     </mrow>
                     <mo accent=true>¯</mo>
                  </mover>
               </mrow>
               <mrow>
                  <mn>1</mn>
                  <mo class=MathClass-bin>−</mo>
                  <mover class=mml-overline accent=false>
                     <mrow>
                        <mi>α</mi>
                     </mrow>
                     <mo accent=true>¯</mo>
                  </mover>
               </mrow>
            </mfrac>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(9)
</table>
<p>is called the <strong><span class=cmbx-10>room constant</span></strong>.<br class=newline>
<p>
   It depends solely on the properties of the room, namely, its size expressed through total surface area
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>S</mi>
   </math>
   , and the implemented acoustic treatment, expressed through the average absorption coefficient
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mover class=mml-overline accent=false>
         <mrow>
            <mi>α</mi>
         </mrow>
         <mo accent=true>¯</mo>
      </mover>
   </math>
   .<br class=newline>
<p>The energy density of the diffuse sound can now be written as:
<table class=equation>
   <tr>
      <td>
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <msub>
               <mrow>
                  <mi>ω</mi>
               </mrow>
               <mrow>
                  <mi>r</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mn>4</mn>
                  <mi>P</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>cR</mi>
               </mrow>
            </mfrac>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(10)
</table>
<h5 class=subsubsectionHead>What does reverberant sound depend on?</h5>
<p>The governing equations (7) and (10) suggest that <strong><span class=cmbx-10>the “loudness” of reverberant </span><span class=cmbx-10>sound</span></strong>:
<ul class=itemize1>
   <li class=itemize><strong><span class=cmbx-10>does not depend on the distance from the source</span></strong>; it is the same in the entire room
   <li class=itemize><strong><span class=cmbx-10>is proportional to the sound power of the source</span></strong>; the stronger the source, the louder the sound everywhere in the room
   <li class=itemize><strong><span class=cmbx-10>does not depend on the directional properties of the source</span></strong>; it is not important in what directions the sound was introduced into the room, only how much of it was introduced
   <li class=itemize><strong><span class=cmbx-10>depends on the properties of the room</span></strong> (amount of absorption); with more absorption in the room, the reverberant sound will be “softer”
</ul>
<p><strong><span class=cmbx-10>The overall conclusion is that the reverberant sound field depends only </span><span class=cmbx-10>on the power of the source, and on the amount of absorption in the </span><span class=cmbx-10>room.</span></strong>
<h3 class=sectionHead>The boundary between direct and reverberant field - reverberation radius and critical distance</h3>
<p>The direct and the reverberant sound field both exist simultaneously in the room, in the sense that a source placed in a room will create both components of the overall sound.<br class=newline>
<p>The direct sound is the strongest close to the source and then decreases with distance, whereas the reverberant sound is constant in the entire room. The assumption can be made that there will be two distinct regions in the room; one with dominant direct sound close to the source, and the other with dominant reverberant sound in the rest of the room.<br class=newline>
<p>The boundary between these two regions is essentially a closed surface that encompasses the source, and is represented with the distance from the source to that surface in any given direction.<br class=newline>
<p>
   An <strong><span class=cmbx-10>omnidirectional source</span></strong> radiates evenly in all directions, and the direct sound at a fixed distance from such a source is equal in all directions, but it drops with distance. The specific distance at which the direct sound becomes equal to the reverberant sound is constant. The boundary surface is a sphere, and the radius of that sphere is called the <strong><span class=cmbx-10>reverberation radius</span></strong> or
   <strong>
      <span class=cmbx-10>hall radius</span>
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi>r</mi>
            </mrow>
            <mrow>
               <mi>H</mi>
            </mrow>
         </msub>
      </math>
   </strong>
   . In other words, if a listener is standing at the distance that matches the reverberation radius (in any direction), they will experience a balance between the direct and reverberant sound.<br class=newline>
<p>
   A <strong><span class=cmbx-10>directional source</span></strong> does not radiate evenly in all directions. Certain direction(s) is/are favoured at the expense of others. This means that the source will produce loud direct sound in the favoured directions, and soft one in the unfavoured ones. The distance required for the direct sound to drop to the level of reverberant sound will be different for each direction. i.e. it will be direction-dependent. The shape of the boundary surface will resemble the directivity pattern of the source, which shows how much energy the source radiates in a given direction. This changing boundary distance is called the
   <strong>
      <span class=cmbx-10>critical distance</span>
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi>D</mi>
            </mrow>
            <mrow>
               <mi>c</mi>
            </mrow>
         </msub>
      </math>
   </strong>
   . A listener standing at a critical distance away from the source (in a given direction) will experience balanced direct and reverberant sound. In favoured direction, the direct sound is loud, so the listener will have to move away to a considerable distance to experience this balance. In the unfavoured directions, the direct sound is weak, so the listener can have this experience fairly close to the source.
<h4 class=subsectionHead>
   How do we find the reverberation radius
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>r</mi>
         </mrow>
         <mrow>
            <mi>H</mi>
         </mrow>
      </msub>
   </math>
   ?
</h4>
<p>The reverberation radius can be found simply by equating the governing equation for energy density of direct sound of an omnidirectional source (3) and the corresponding equation for reverberant sound (10), as
<table class=equation>
   <tr>
      <td>
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <msub>
               <mrow>
                  <mi>ω</mi>
               </mrow>
               <mrow>
                  <mi>d</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mi>P</mi>
               </mrow>
               <mrow>
                  <mn>4</mn>
                  <mi>π</mi>
                  <msubsup>
                     <mrow>
                        <mi>r</mi>
                     </mrow>
                     <mrow>
                        <mi>H</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msubsup>
                  <mi>c</mi>
               </mrow>
            </mfrac>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mn>4</mn>
                  <mi>P</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>cR</mi>
               </mrow>
            </mfrac>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>ω</mi>
               </mrow>
               <mrow>
                  <mi>r</mi>
               </mrow>
            </msub>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(11)
</table>
<p>
   This leads to the value of <strong><span class=cmbx-10>the reverberation radius</span></strong>
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>r</mi>
         </mrow>
         <mrow>
            <mi>H</mi>
         </mrow>
      </msub>
   </math>
   as the distance from the omnidirectional source at which direct and reverberant sound are equal, as
<table class=equation>
   <tr>
      <td>
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <msub>
               <mrow>
                  <mi>r</mi>
               </mrow>
               <mrow>
                  <mi>H</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mn>1</mn>
               </mrow>
               <mrow>
                  <mn>4</mn>
               </mrow>
            </mfrac>
            <msqrt>
               <mrow>
                  <mfrac>
                     <mrow>
                        <mi>R</mi>
                     </mrow>
                     <mrow>
                        <mi>π</mi>
                     </mrow>
                  </mfrac>
               </mrow>
            </msqrt>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(12)
</table>
<p>
   <strong><span class=cmbx-10>For Sabine rooms</span></strong> that have a reasonably small average absorption coefficient, the reverberation radius is usually obtained by equating equation (3) with equation (7), i.e. without the correction introduced in equation (8) that leads to equation (10). This yields <strong><span class=cmbx-10>the reverberation radius</span></strong>
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>r</mi>
         </mrow>
         <mrow>
            <mi>H</mi>
         </mrow>
      </msub>
   </math>
   as
<table class=equation>
   <tr>
      <td style="font-size: 13px">
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <msub>
               <mrow>
                  <mi>r</mi>
               </mrow>
               <mrow>
                  <mi>H</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mn>1</mn>
               </mrow>
               <mrow>
                  <mn>4</mn>
               </mrow>
            </mfrac>
            <msqrt>
               <mrow>
                  <mfrac>
                     <mrow>
                        <mi>A</mi>
                     </mrow>
                     <mrow>
                        <mi>π</mi>
                     </mrow>
                  </mfrac>
               </mrow>
            </msqrt>
            <mo class=MathClass-rel>=</mo>
            <mn>0</mn>
            <mo class=MathClass-punc>.</mo>
            <mn>057</mn>
            <msqrt>
               <mrow>
                  <mfrac>
                     <mrow>
                        <mi>V</mi>
                     </mrow>
                     <mrow>
                        <mi>R</mi>
                        <msub>
                           <mrow>
                              <mi>T</mi>
                           </mrow>
                           <mrow>
                              <mn>60</mn>
                           </mrow>
                        </msub>
                     </mrow>
                  </mfrac>
               </mrow>
            </msqrt>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(13)
</table>
<p>
   The right part of equation (13) is obtained by using the Sabine formula for reverberation time to express the total absorption area of the room
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>A</mi>
   </math>
   through the volume of the room
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>V</mi>
   </math>
   and the reverberation time
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>R</mi>
      <msub>
         <mrow>
            <mi>T</mi>
         </mrow>
         <mrow>
            <mn>60</mn>
         </mrow>
      </msub>
   </math>
   . Equation (13) is widely used for determining the reverberation radius of a room because it can be calculated simply by measuring or assessing the room volume and by measuring the reverberation time in the room. The error made in the given conditions is acceptable.<br class=newline>
<p>
   Equations (12) and (13) both show that the reverberation radius of a room depends only on the properties of the room, expressed either by total absorption area
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>A</mi>
   </math>
   or the room constant
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>R</mi>
   </math>
   . This parameter is frequency-dependent, as the absorption in the room changes with frequency. On the other hand, it does not depend on direction, i.e. its value is constant, as the source radiates equally in all directions.<br class=newline>
<p>
   Therefore, for an omnidirectional source, the boundary between the direct and reverberant field is essentially a sphere with the radius equal to the reverberation radius
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>r</mi>
         </mrow>
         <mrow>
            <mi>H</mi>
         </mrow>
      </msub>
   </math>
   in a given frequency band.
<h4 class=subsectionHead>
   How do we find critical distance
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>D</mi>
         </mrow>
         <mrow>
            <mi>c</mi>
         </mrow>
      </msub>
   </math>
   ?
</h4>
<p>The critical distance is found by equating the energy density of direct sound of a directional source and the energy density of reverberant sound [1], i.e. equation (6) is equated with equation (10), as
<table class=equation>
   <tr>
      <td>
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <msub>
               <mrow>
                  <mi>ω</mi>
               </mrow>
               <mrow>
                  <mi>d</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mi mathvariant=italic>PQ</mi>
               </mrow>
               <mrow>
                  <mn>4</mn>
                  <mi>π</mi>
                  <msubsup>
                     <mrow>
                        <mi>D</mi>
                     </mrow>
                     <mrow>
                        <mi>c</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msubsup>
                  <mi>c</mi>
               </mrow>
            </mfrac>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mn>4</mn>
                  <mi>P</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>cR</mi>
               </mrow>
            </mfrac>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>ω</mi>
               </mrow>
               <mrow>
                  <mi>r</mi>
               </mrow>
            </msub>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(14)
</table>
<p>
   This yields <strong><span class=cmbx-10>the critical distance</span></strong>
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>D</mi>
         </mrow>
         <mrow>
            <mi>c</mi>
         </mrow>
      </msub>
   </math>
   as
<table class=equation>
   <tr>
      <td>
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <msub>
               <mrow>
                  <mi>D</mi>
               </mrow>
               <mrow>
                  <mi>c</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mn>1</mn>
               </mrow>
               <mrow>
                  <mn>4</mn>
               </mrow>
            </mfrac>
            <msqrt>
               <mrow>
                  <mfrac>
                     <mrow>
                        <mi mathvariant=italic>RQ</mi>
                     </mrow>
                     <mrow>
                        <mi>π</mi>
                     </mrow>
                  </mfrac>
               </mrow>
            </msqrt>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>r</mi>
               </mrow>
               <mrow>
                  <mi>H</mi>
               </mrow>
            </msub>
            <msqrt>
               <mrow>
                  <mi>Q</mi>
               </mrow>
            </msqrt>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(15)
</table>
<p>For Sabine rooms that have a reasonably small average absorption coefficient, the critical distance can be obtained by equating equation (6) with equation (7), which yields
<table class=equation>
   <tr>
      <td>
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <msub>
               <mrow>
                  <mi>D</mi>
               </mrow>
               <mrow>
                  <mi>c</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mn>1</mn>
               </mrow>
               <mrow>
                  <mn>4</mn>
               </mrow>
            </mfrac>
            <msqrt>
               <mrow>
                  <mfrac>
                     <mrow>
                        <mi mathvariant=italic>AQ</mi>
                     </mrow>
                     <mrow>
                        <mi>π</mi>
                     </mrow>
                  </mfrac>
               </mrow>
            </msqrt>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>r</mi>
               </mrow>
               <mrow>
                  <mi>H</mi>
               </mrow>
            </msub>
            <msqrt>
               <mrow>
                  <mi>Q</mi>
               </mrow>
            </msqrt>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(16)
</table>
<p>
   Equations (15) and (16) have identical form, showing that the boundary between the direct and reverberant field in a room expressed through the critical distance depends on the properties of the room contained in the reverberation radius
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>r</mi>
         </mrow>
         <mrow>
            <mi>H</mi>
         </mrow>
      </msub>
   </math>
   (no matter how it was obtained), but also on the directional properties of the source displayed through its directivity factor
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>Q</mi>
   </math>
   .<br class=newline>
<p>As the directivity factor was originally defined as direction-dependent, thus covering the entire solid angle the source radiates into, the value of critical distance will also be direction-dependent. Specifically, in the region of space to which the source radiates most of its energy, the directivity factor will be greater than 1, and the boundary between the direct and reverberant sound will shift to the critical distance larger than the reverberation radius. In all other directions the source will emit little direct energy, and its directivity factor will be smaller than 1, yielding a critical distance smaller than the reverberation radius.
<h4 class=subsectionHead>What does the boundary between direct and reverberant field depend on?</h4>
<p>Equations (12), (13), (15) and (16) suggest that <strong><span class=cmbx-10>the form of the boundary </span><span class=cmbx-10>between direct and reverberant field depends on</span></strong>:
<ul class=itemize1>
   <li class=itemize><strong><span class=cmbx-10>directional properties of the source</span></strong>; the louder the sound produced by the source in a given direction, the farther away from the source the boundary will be in that direction
   <li class=itemize><strong><span class=cmbx-10>amount of absorption in the room</span></strong>; more absorption put in the room weakens the reverberant sound, and the boundary is automatically moved farther away from the source in all directions
</ul>
<h3 class=sectionHead>Sound pressure level in the room</h3>
<p>The sound pressure level is closely associated with the perception of loudness of a sound. There are two distinct components of the overall sound in the room: the direct sound close to the source, and reverberant sound in the rest of the room. The overall sound pressure level in the room is found by adding up these components.<br class=newline>
<p>If a listener is standing very close to the source and then starts to move away from it, they can expect that the sound pressure level of the sound in a room will be dropping by 6 dB for every doubling of distance, and it will keep dropping until the listener reaches the boundary between the direct and reverberant field. By continuing to move away, the listener will enter the reverberant field, in which the sound pressure level remains constant.
<h4 class=subsectionHead>How do we calculate direct, reverberant and overall sound pressure level in a room?</h4>
<p>The distribution of sound pressure in the room can be calculated from energy density, having in mind the relationship between the two parameters, as
<table class=equation>
   <tr>
      <td>
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <msup>
               <mrow>
                  <mi>p</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msup>
            <mo class=MathClass-rel>=</mo>
            <mi mathvariant=italic>ωρ</mi>
            <msup>
               <mrow>
                  <mi>c</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msup>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(17)
</table>
<p>
   where
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msup>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msup>
   </math>
   is the mean square of the sound pressure,
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>ω</mi>
   </math>
   is the energy density,
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>ρ</mi>
   </math>
   is the density of air, and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>c</mi>
   </math>
   is the speed of sound.<br class=newline>
<p>
   Total energy density in the room
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>ω</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>tot</mi>
         </mrow>
      </msub>
   </math>
   is the sum of the energy density of direct sound and the one of reverberant sound, i.e. in the general case of a directional source and with room correction applied, it yields
<table class=equation>
   <tr>
      <td style="font-size: 7px">
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <msub>
               <mrow>
                  <mi>ω</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>tot</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>ω</mi>
               </mrow>
               <mrow>
                  <mi>d</mi>
               </mrow>
            </msub>
            <mo class=MathClass-bin>+</mo>
            <msub>
               <mrow>
                  <mi>ω</mi>
               </mrow>
               <mrow>
                  <mi>r</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mi mathvariant=italic>PQ</mi>
               </mrow>
               <mrow>
                  <mn>4</mn>
                  <mi>π</mi>
                  <msup>
                     <mrow>
                        <mi>r</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
                  <mi>c</mi>
               </mrow>
            </mfrac>
            <mo class=MathClass-bin>+</mo>
            <mfrac>
               <mrow>
                  <mn>4</mn>
                  <mi>P</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>cR</mi>
               </mrow>
            </mfrac>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mi>P</mi>
               </mrow>
               <mrow>
                  <mi>c</mi>
               </mrow>
            </mfrac>
            <mstyle mathsize=1.61em>
               <mrow>
                  <mo form=prefix fence=true>(</mo>
               </mrow>
            </mstyle>
            <mfrac>
               <mrow>
                  <mi>Q</mi>
               </mrow>
               <mrow>
                  <mn>4</mn>
                  <mi>π</mi>
                  <msup>
                     <mrow>
                        <mi>r</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
               </mrow>
            </mfrac>
            <mo class=MathClass-bin>+</mo>
            <mfrac>
               <mrow>
                  <mn>4</mn>
               </mrow>
               <mrow>
                  <mi>R</mi>
               </mrow>
            </mfrac>
            <mstyle mathsize=1.61em>
               <mrow>
                  <mo form=prefix fence=true>)</mo>
               </mrow>
            </mstyle>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(18)
</table>
<p>Taking into account the relationship shown in equation (17), the total mean square sound pressure in the room can be obtained as
<table class=equation>
   <tr>
      <td style="font-size: 7px">
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <msubsup>
               <mrow>
                  <mi>p</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>tot</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msubsup>
            <mo class=MathClass-rel>=</mo>
            <msubsup>
               <mrow>
                  <mi>p</mi>
               </mrow>
               <mrow>
                  <mi>d</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msubsup>
            <mo class=MathClass-bin>+</mo>
            <msubsup>
               <mrow>
                  <mi>p</mi>
               </mrow>
               <mrow>
                  <mi>r</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msubsup>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mi mathvariant=italic>PQρc</mi>
               </mrow>
               <mrow>
                  <mn>4</mn>
                  <mi>π</mi>
                  <msup>
                     <mrow>
                        <mi>r</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
               </mrow>
            </mfrac>
            <mo class=MathClass-bin>+</mo>
            <mfrac>
               <mrow>
                  <mn>4</mn>
                  <mi mathvariant=italic>Pρc</mi>
               </mrow>
               <mrow>
                  <mi>R</mi>
               </mrow>
            </mfrac>
            <mo class=MathClass-rel>=</mo>
            <mi mathvariant=italic>Pρc</mi>
            <mstyle mathsize=1.61em>
               <mrow>
                  <mo form=prefix fence=true>(</mo>
               </mrow>
            </mstyle>
            <mfrac>
               <mrow>
                  <mi>Q</mi>
               </mrow>
               <mrow>
                  <mn>4</mn>
                  <mi>π</mi>
                  <msup>
                     <mrow>
                        <mi>r</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
               </mrow>
            </mfrac>
            <mo class=MathClass-bin>+</mo>
            <mfrac>
               <mrow>
                  <mn>4</mn>
               </mrow>
               <mrow>
                  <mi>R</mi>
               </mrow>
            </mfrac>
            <mstyle mathsize=1.61em>
               <mrow>
                  <mo form=prefix fence=true>)</mo>
               </mrow>
            </mstyle>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(19)
</table>
<p>The first component in equation (19) is the mean square of direct sound pressure, and the second one is the mean square of reverberant sound pressure.<br class=newline>
<p>The <strong><span class=cmbx-10>direct sound pressure level</span></strong> can be derived from equation (19) and expressed as
<table class=equation>
   <tr>
      <td style="font-size: 14px">
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <msub>
               <mrow>
                  <mi>L</mi>
               </mrow>
               <mrow>
                  <mi>d</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>L</mi>
               </mrow>
               <mrow>
                  <mi>W</mi>
               </mrow>
            </msub>
            <mo class=MathClass-bin>+</mo>
            <msub>
               <mrow>
                  <mi>D</mi>
               </mrow>
               <mrow>
                  <mi>i</mi>
               </mrow>
            </msub>
            <mo class=MathClass-bin>−</mo>
            <mn>20</mn>
            <mi class=qopname>log</mi>
            <mo>⁡</mo>
            <mi>r</mi>
            <mo class=MathClass-bin>−</mo>
            <mn>11</mn>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(20)
</table>
<p>The <strong><span class=cmbx-10>reverberant sound pressure level</span></strong> can also be derived from equation (19) and expressed as
<table class=equation>
   <tr>
      <td>
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <msub>
               <mrow>
                  <mi>L</mi>
               </mrow>
               <mrow>
                  <mi>r</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>L</mi>
               </mrow>
               <mrow>
                  <mi>W</mi>
               </mrow>
            </msub>
            <mo class=MathClass-bin>−</mo>
            <mn>10</mn>
            <mi class=qopname>log</mi>
            <mo>⁡</mo>
            <mi>R</mi>
            <mo class=MathClass-bin>+</mo>
            <mn>6</mn>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(21)
</table>
<p>The <strong><span class=cmbx-10>total sound pressure level</span></strong> can be expressed as
<table class=equation>
   <tr>
      <td style="font-size: 11px">
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <msub>
               <mrow>
                  <mi>L</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>tot</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>L</mi>
               </mrow>
               <mrow>
                  <mi>W</mi>
               </mrow>
            </msub>
            <mo class=MathClass-bin>+</mo>
            <mn>10</mn>
            <mi class=qopname>log</mi>
            <mo>⁡</mo>
            <mstyle mathsize=1.61em>
               <mrow>
                  <mo form=prefix fence=true>(</mo>
               </mrow>
            </mstyle>
            <mfrac>
               <mrow>
                  <mi>Q</mi>
               </mrow>
               <mrow>
                  <mn>4</mn>
                  <mi>π</mi>
                  <msup>
                     <mrow>
                        <mi>r</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
               </mrow>
            </mfrac>
            <mo class=MathClass-bin>+</mo>
            <mfrac>
               <mrow>
                  <mn>4</mn>
               </mrow>
               <mrow>
                  <mi>R</mi>
               </mrow>
            </mfrac>
            <mstyle mathsize=1.61em>
               <mrow>
                  <mo form=prefix fence=true>)</mo>
               </mrow>
            </mstyle>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(22)
</table>
<p>
   In equations (20-22),
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>L</mi>
         </mrow>
         <mrow>
            <mi>w</mi>
         </mrow>
      </msub>
   </math>
   is the sound power level of the source in dBre1pW,
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>D</mi>
         </mrow>
         <mrow>
            <mi>i</mi>
         </mrow>
      </msub>
   </math>
   is the directivity index in dB, calculated as
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>D</mi>
         </mrow>
         <mrow>
            <mi>i</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mn>10</mn>
      <mi class=qopname>log</mi>
      <mo>⁡</mo>
      <mi>Q</mi>
   </math>
   ,
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>r</mi>
   </math>
   is the distance from the source, and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>R</mi>
   </math>
   is the room constant. For Sabine rooms with small average absorption coefficient,
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>R</mi>
   </math>
   can be replaced with total absorption in the room
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>A</mi>
   </math>
   .<br class=newline>
<p>
   It is important to note that the directivity factor
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>Q</mi>
   </math>
   in equation (22) and the corresponding directivity index
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>D</mi>
         </mrow>
         <mrow>
            <mi>i</mi>
         </mrow>
      </msub>
   </math>
   in equation (20) are direction-dependent, so they will influence the level of direct sound as a function of direction at a given distance from the source. The critical distance
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>D</mi>
         </mrow>
         <mrow>
            <mi>c</mi>
         </mrow>
      </msub>
   </math>
   will be affected as well.<br class=newline>
<p>
   An example of direct, reverberant and total sound pressure level in a room is shown in <a href=# data-img-alt="Figure 8. The change of direct (blue), reverberant (green) and total (red) sound pressure level with the distance from the source." data-img-src=${config.ASSETS_URL}/05/06/theory/figure-8.svg>Figure 8</a>.
   <figure class=figure>
<p><img alt="Figure 8. The change of direct (blue), reverberant (green) and total (red) sound pressure level with the distance from the source." src=${config.ASSETS_URL}/05/06/theory/figure-8.svg><figcaption class=caption>Figure 8. The change of direct (blue), reverberant (green) and total (red) sound pressure level with the distance from the source.</figcaption></figure>
<h3 class=sectionHead>Illustrations of sound distribution in a room</h3>
<p>
   In <a href=# data-img-alt="Figure 9. On the left: the directivity index of an omnidirectional and a directional source with the same sound power, observed in three directions (T1, T2, and T3); on the right: the change of the relationship between the direct (red and blue) and reverberant (black) sound pressure level, and the corresponding critical distance depending on the observed direction." data-img-src=${config.ASSETS_URL}/05/06/theory/figure-9.svg>Figure 9</a>, two sources with the same sound power
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>P</mi>
   </math>
   are displayed. One source is omnidirectional (blue), and the other one is a directional dipole source (red) with a figure-of-eight radiation pattern. Three specific directions are observed (T1, T2 and T3).<br class=newline>
<p>In the direction T1, the directional source gives the maximum amount of direct sound, which is 10 dB higher than the one produced by the omnidirectional source. In direction T2, the direct sound pressure level of the directional source is still higher by about 3 dB, but in direction T3 the directional source produces direct sound whose sound pressure level is now 8 dB lower than the one produced by the omnidirectional source. At the same time, the sound pressure level of reverberant sound is constant and equal for both sources, as they have the same sound power.<br class=newline>
<p>
   The right side of <a href=# data-img-alt="Figure 9. On the left: the directivity index of an omnidirectional and a directional source with the same sound power, observed in three directions (T1, T2, and T3); on the right: the change of the relationship between the direct (red and blue) and reverberant (black) sound pressure level, and the corresponding critical distance depending on the observed direction." data-img-src=${config.ASSETS_URL}/05/06/theory/figure-9.svg>Figure 9</a> shows that the relationship between direct and reverberant sound pressure level will change depending on the observed direction due to the directional properties of the source. In direction T1, direct sound is the strongest, much stronger than the one produced by the omnidirectional source. Therefore, it will be projected quite far before being overpowered by reverberant sound. The critical distance
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>D</mi>
         </mrow>
         <mrow>
            <mi>c</mi>
            <mn>1</mn>
         </mrow>
      </msub>
   </math>
   is the largest in this case. In direction T2, the directional source still gives a bit more direct sound than the omnidirectional source. Therefore, in this direction the critical distance
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>D</mi>
         </mrow>
         <mrow>
            <mi>c</mi>
            <mn>2</mn>
         </mrow>
      </msub>
   </math>
   will be just a bit larger than the reverberation radius
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>r</mi>
         </mrow>
         <mrow>
            <mi>H</mi>
         </mrow>
      </msub>
   </math>
   . In direction T3, the directional source emits very little direct sound, much less than the omnidirectional source. Therefore, the direct sound will be weak and quickly overpowered by the reverberant sound, making the critical distance
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>D</mi>
         </mrow>
         <mrow>
            <mi>c</mi>
            <mn>3</mn>
         </mrow>
      </msub>
   </math>
   quite small.
   <figure class=figure>
<p><img alt="Figure 9. On the left: the directivity index of an omnidirectional and a directional source with the same sound power, observed in three directions (T1, T2, and T3); on the right: the change of the relationship between the direct (red and blue) and reverberant (black) sound pressure level, and the corresponding critical distance depending on the observed direction." src=${config.ASSETS_URL}/05/06/theory/figure-9.svg><figcaption class=caption>Figure 9. On the left: the directivity index of an omnidirectional and a directional source with the same sound power, observed in three directions (T1, T2, and T3); on the right: the change of the relationship between the direct (red and blue) and reverberant (black) sound pressure level, and the corresponding critical distance depending on the observed direction.</figcaption></figure>
<p>The conclusion is that the directivity of the source can significantly change the ratio of direct to reverberant sound at a given distance from the source, depending on direction. The same can be concluded for the distance between the source and the listener, observed for a given direction. On the other hand, changing the power of the source will have no effect on the relationship between direct and reverberant sound, observed at any location in the room. For example, if the sound power level of the source is raised by 10 dB, both the direct and reverberant sound will be raised in level by 10 dB, leaving their relationship unchanged.<br class=newline>
<p>Another illustration of the interaction of a sound source with a room is shown in Figures <a href=# data-img-alt="Figure 10. The directivity index of the omnidirectional (red) and the directional (blue) source." data-img-src=${config.ASSETS_URL}/05/06/theory/figure-10.svg>10</a>, <a href=# data-img-alt="Figure 11. On the left: the changing sound pressure level of direct sound (from red to dark blue), and the constant sound pressure level of reverberant sound (blue), produced by an omnidirectional source; on the right: total sound pressure level." data-img-src=${config.ASSETS_URL}/05/06/theory/figure-11.svg>11</a> and <a href=# data-img-alt="Figure 12. On the left: the changing sound pressure level of direct sound (from red to dark blue), and the constant sound pressure level of reverberant sound (light blue), produced by a directional source; on the right: total sound pressure level." data-img-src=${config.ASSETS_URL}/05/06/theory/figure-12.svg>12</>. <a href=# data-img-alt="Figure 10. The directivity index of the omnidirectional (red) and the directional (blue) source." data-img-src=${config.ASSETS_URL}/05/06/theory/figure-10.svg>Figure 10</a> shows the directivity index of two sources: an omnidirectional source and a directional one. Both of them have the same sound power. It can be seen that the directional source delivers most of its energy to the front, and very little to the back. Compared to the omnidirectional source, the directional one will be “louder” by 7 dB in the frontal direction, but also 11 dB “quieter” behind.<br class=newline>
<p>
   As an example, both sources are placed in the center of a room that is 12 m x 12 m x 3.5 m large, with the directional source facing to the left. The direct, reverberant and total sound pressure level produced by the omnidirectional and the directional source are shown in Figures <a href=# data-img-alt="Figure 11. On the left: the changing sound pressure level of direct sound (from red to dark blue), and the constant sound pressure level of reverberant sound (blue), produced by an omnidirectional source; on the right: total sound pressure level." data-img-src=${config.ASSETS_URL}/05/06/theory/figure-11.svg>11</a> and <a href=# data-img-alt="Figure 12. On the left: the changing sound pressure level of direct sound (from red to dark blue), and the constant sound pressure level of reverberant sound (light blue), produced by a directional source; on the right: total sound pressure level." data-img-src=${config.ASSETS_URL}/05/06/theory/figure-12.svg>12</a>, respectively.
   <figure class=figure>
<p><img alt="Figure 10. The directivity index of the omnidirectional (red) and the directional (blue) source." src=${config.ASSETS_URL}/05/06/theory/figure-10.svg><figcaption class=caption>Figure 10. The directivity index of the omnidirectional (red) and the directional (blue) source.</figcaption></figure><figure class=figure><p><img alt="Figure 11. On the left: the changing sound pressure level of direct sound (from red to dark blue), and the constant sound pressure level of reverberant sound (blue), produced by an omnidirectional source; on the right: total sound pressure level." src=${config.ASSETS_URL}/05/06/theory/figure-11.svg><figcaption class=caption>Figure 11. On the left: the changing sound pressure level of direct sound (from red to dark blue), and the constant sound pressure level of reverberant sound (blue), produced by an omnidirectional source; on the right: total sound pressure level.</figcaption></figure>
<p><a href=# data-img-alt="Figure 11. On the left: the changing sound pressure level of direct sound (from red to dark blue), and the constant sound pressure level of reverberant sound (blue), produced by an omnidirectional source; on the right: total sound pressure level." data-img-src=${config.ASSETS_URL}/05/06/theory/figure-11.svg>Figure 11</a> shows that an omnidirectional source will produce direct sound that drops equally in all directions as the listener moves away from the source. The level of reverberant sound is constant in the entire room. The positions where direct and reverberant sound pressure level are equal form a silhouette of a circle. The radius of this circle is, in fact, the reverberation radius of the room. The total sound pressure level suggests that the direct sound has a dominant influence in the vicinity of the source, and that it drops equally in all directions, before being overpowered by reverberant sound.<br class=newline>
<p>
   <a href=# data-img-alt="Figure 12. On the left: the changing sound pressure level of direct sound (from red to dark blue), and the constant sound pressure level of reverberant sound (light blue), produced by a directional source; on the right: total sound pressure level." data-img-src=${config.ASSETS_URL}/05/06/theory/figure-12.svg>Figure 12</a> shows that the directional source will produce much louder direct sound to the front than to the back. The reverberant sound is again constant in the entire room. The boundary of the direct and reverberant field forms a silhouette that resembles the shape of the directivity index pattern of the source. That silhouette shows how the critical distance changes depending on direction; it will be fairly large in front of the source (and to the sides), but very small behind it. This means that the direct sound will be heard louder and clearer by the listener standing in front of the source, even at a considerable distance. The listener standing behind the source will be immersed in reverberant sound even close to the source, as there is very little direct sound emitted in that direction. The total sound pressure level also shows that the directional source will have a large influence in the frontal direction, where it emits most of its energy, but very little or no influence to the back, where it emits very little.
   <figure class=figure>
<p><img alt="Figure 12. On the left: the changing sound pressure level of direct sound (from red to dark blue), and the constant sound pressure level of reverberant sound (light blue), produced by a directional source; on the right: total sound pressure level." src=${config.ASSETS_URL}/05/06/theory/figure-12.svg><figcaption class=caption>Figure 12. On the left: the changing sound pressure level of direct sound (from red to dark blue), and the constant sound pressure level of reverberant sound (light blue), produced by a directional source; on the right: total sound pressure level.</figcaption></figure>
<h3 class=sectionHead>Reverberant sound field in real rooms</h3>
<p>At the beginning of this lesson, it was assumed that a perfect diffuse field is formed in a room, in the sense that there are enough sound reflections that arrive to a given point in space, and that they arrive from completely random directions.<br class=newline>
<p>In such a sound field, it would be impossible to locate the sound source, and the sound pressure level of the reverberant sound would be constant everywhere in the room.<br class=newline>
<p>Acoustic conditions in real rooms show that a (nearly) perfect diffuse field can be formed only in highly reverberant spaces. Such spaces were either designed for a specific purpose (reverberant chambers), or are the result of flaws or lapses in the design stage, thus having little or no value for practical use (e.g. modern churches made of smooth, painted concrete).<br class=newline>
<p>In rooms that are acoustically treated to serve any reasonable purpose, the number of reflections from their surfaces is too small for the perfect diffuse field to form properly. Additionally, the sound field far from the source is perhaps influenced less by the true reverberation tail, and more by early reflections, which are not random in relation to the time and direction of arrival.<br class=newline>
<p>Various sources in literature, which deal with corrections of the traditional approach to the sound fields in a closed space, show that the sound pressure level of reverberant sound is not constant throughout the room. Instead, it tends to decrease with the distance from the source, but at a much slower rate than direct sound.<br class=newline>
<p>For Sabine rooms, including concert halls and auditoria, the level correction introduced by Barron [2] suggests a linear drop of reverberant sound level with the distance from the source, which tends to be more pronounced in acoustically treated rooms. The correction is applied to equation (21), which leads to
<table class=equation>
   <tr>
      <td style="font-size: 11px">
         <math class=equation display=block xmlns=http://www.w3.org/1998/Math/MathML>
            <msub>
               <mrow>
                  <mi>L</mi>
               </mrow>
               <mrow>
                  <mi>r</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>L</mi>
               </mrow>
               <mrow>
                  <mi>W</mi>
               </mrow>
            </msub>
            <mo class=MathClass-bin>−</mo>
            <mn>10</mn>
            <mi class=qopname>log</mi>
            <mo>⁡</mo>
            <mi>R</mi>
            <mo class=MathClass-bin>+</mo>
            <mn>6</mn>
            <mo class=MathClass-bin>−</mo>
            <mn>0</mn>
            <mo class=MathClass-punc>.</mo>
            <mn>174</mn>
            <mfrac>
               <mrow>
                  <mi>r</mi>
               </mrow>
               <mrow>
                  <mi>R</mi>
                  <msub>
                     <mrow>
                        <mi>T</mi>
                     </mrow>
                     <mrow>
                        <mn>60</mn>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <mo class=MathClass-punc></mo>
         </math>
      <td class=eq-no>(23)
</table>
<p>
   In the correction above,
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>r</mi>
   </math>
   is the distance from the source, and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>R</mi>
      <msub>
         <mrow>
            <mi>T</mi>
         </mrow>
         <mrow>
            <mn>60</mn>
         </mrow>
      </msub>
   </math>
   is the reverberation time in the room. For example, in a concert hall with the reverberation time of 2 seconds, the expected drop will be about 3 dB for the farthest seats, if they are about 35 meters away from the source/stage.<br class=newline>
<p>In rooms that are of non-Sabine type, meaning that one dimension is much smaller or much bigger than the other two (e.g. an industrial hall or a long hallway, respectively), and/or the acoustic absorption is distributed unevenly, the behaviour of reverberant sound will deviate even more from the constant value calculated through the traditional approach. Different literature mentions a reverberant sound level drop that goes up to 3 dB per doubling of distance. For an uneven distribution of elements used for acoustic treatment, the room itself can cause the reverberant sound to be direction-dependent, thus making the hall radius change depending on the direction in which it is observed. This has nothing to do with source directivity; it represents only the influence of the room.<br class=newline>
<p>
   The difference between the traditional approach and the Barron refinement is shown in <a href=# data-img-alt="Figure 13. The difference in reverberant sound level calculated according to traditional theory (constant) and with Barron correction (drops with distance)." data-img-src=${config.ASSETS_URL}/05/06/theory/figure-13.svg>Figure 13</a>. The example is made for a room that is 20 meters long, 20 meters wide, and 3.5 meters high, and for three average absorption coefficients, ranging from 0.05 (low absorption) to 0.20 (moderate absorption). The chart shows that the drop in reverberant sound pressure level is indeed linear with the distance from the source. In case of low average absorption in the room (strong reverberation), the difference is very small, indicating the formation of a near-perfect diffuse field. With the increase of average absorption in the room (by adding acoustic treatment), the drop becomes steeper, and the difference larger, indicating that the diffuse field in the room is further and further away from the ideal one.
   <figure class=figure>
<p><img alt="Figure 13. The difference in reverberant sound level calculated according to traditional theory (constant) and with Barron correction (drops with distance)." src=${config.ASSETS_URL}/05/06/theory/figure-13.svg><figcaption class=caption>Figure 13. The difference in reverberant sound level calculated according to traditional theory (constant) and with Barron correction (drops with distance).</figcaption></figure>
<p>For a basic understanding of the interaction of a sound source with a room, the traditional approach offers a good basis, while the deviations described here belong to the realm of advanced room acoustics.
<p>
<h3 class=likesectionHead>Glossary</h3>
<dl>
  <dt>2D
  <dd>two-dimensional
  <dt>3D
  <dd>three-dimensional
  <dt>HVAC
  <dd>Heating, Ventilation, and Air Conditioning
  <dt>PA
  <dd>public address
</dl>
<h3 class=sectionHead>References</h3>
<dl class=thebibliography>
   <dt class=thebibliography id=X0-Ahnert_Steffen:2017>[1]
   <dd class=thebibliography id=bib-1>
      <p>Wolfgang Ahnert and Frank Steffen. <span class=cmti-10>Sound reinforcement engineering</span>. (1st Edition). CRC Press, 2017. <span class=cmcsc-10><span class=small-caps>isbn</span></span>: 978-1-138-56974-4.
   <dt class=thebibliography id=X0-Barron_2010>[2]
   <dd class=thebibliography id=bib-2>
      <p>Michael Barron. <span class=cmti-10>Auditorium Acoustics and Architectural Design</span>. Second Edition. London / New York: Spon Press, 2010. <span class=cmcsc-10><span class=small-caps>isbn</span></span>: 978-0-415-24510-0.
   <dt class=thebibliography id=X0-Bies_Hansen_2009>[3]
   <dd class=thebibliography id=bib-3>
      <p>David A. Bies and Colin H. Hansen. <span class=cmti-10>Engineering Noise Control – Theory </span><span class=cmti-10>and Practice</span>. (Fourth Edition). London / New York: Spon Press, 2009. <span class=cmcsc-10><span class=small-caps>isbn</span></span>: 978-0-415-48707-8.
</dl>`;

export default theory;
